import styles from './index.module.css';
import { useTranslation } from 'react-i18next';
import { ProcessField, CustomProcessField } from 'types/dataTableTypes';
import { IconName } from 'components/darwin-ui/Icon';
import {
  useCreateProcessField,
  useEditProcessField,
  useGetProcessFields,
  useGetFormsByProcessId,
  useGetCustomFieldsWithFormCount,
} from 'api/services/dataTableService';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useCallback, useEffect, useState, useMemo } from 'react';
import useTableControls from 'hooks/useTableControls';
import { FieldsModal, FormFields } from './FieldsModal';
import { DeleteFieldModal } from './DeleteFieldModal';
import { SystemFieldsTable } from './SystemFieldsTable';
import { CustomFieldsTable } from './CustomFieldsTable';
import { useFieldModals } from 'hooks/dataTable/useFieldModals';
import { createTableChangeHandler, createFieldPayload } from 'utils/dataTable';

interface DataFieldsProps {
  processId: string;
  categoryName: string;
}

export type ColumnAction = {
  permission?: boolean;
  key: string;
  name?: IconName;
  label?: string;
  onClick?: (user: ProcessField | CustomProcessField) => void;
};

export const DataFields = ({ processId, categoryName }: DataFieldsProps) => {
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();

  // Separate table controls for System Fields
  const systemTableControls = useTableControls({
    defaultSortBy: '',
    initialPageSize: 100,
  });

  // Separate table controls for Custom Fields
  const customTableControls = useTableControls({
    defaultSortBy: '',
    initialPageSize: 100,
  });

  const [selectedFormId, setSelectedFormId] = useState<string>();

  const { mutateAsync: createField } = useCreateProcessField();
  const { mutateAsync: editField } = useEditProcessField();

  const {
    fieldModal,
    setFieldModal,
    fieldToDelete,
    setFieldToDelete,
    handleAddField,
    handleEditField,
    handleDeleteField,
  } = useFieldModals();

  // Get custom fields
  const {
    data: customFieldsData,
    isLoading: isCustomFieldsLoading,
    isError: isCustomFieldsError,
  } = useGetCustomFieldsWithFormCount({
    processId: processId || '',
    page: customTableControls.currentPage,
    limit: customTableControls.pageSize,
    query: customTableControls.searchQuery,
    sortOrder: customTableControls.sortOrder,
    formId: selectedFormId,
  });

  // Get system fields
  const {
    data: systemFieldsData,
    isLoading: isSystemFieldsLoading,
    isError: isSystemFieldsError,
  } = useGetProcessFields({
    processId: processId || '',
    page: systemTableControls.currentPage,
    limit: systemTableControls.pageSize,
    query: systemTableControls.searchQuery,
    sortOrder: systemTableControls.sortOrder,
  });

  // Get all forms for the process
  const { data: formsResponse } = useGetFormsByProcessId({
    processId: processId || '',
  });

  const formsData = formsResponse?.data || [];
  const customFields = useMemo(
    () => customFieldsData?.data || [],
    [customFieldsData]
  ) as CustomProcessField[];
  const systemFields = useMemo(
    () => systemFieldsData?.data || [],
    [systemFieldsData]
  );

  useEffect(() => {
    if (isCustomFieldsError || isSystemFieldsError) {
      openNotification(
        t('error'),
        t('data-tables.fields-list.loading-error'),
        'error'
      );
    }
  }, [isCustomFieldsError, isSystemFieldsError, t, openNotification]);

  // Form selection handler
  const handleFormSelect = useCallback((formId: string) => {
    setSelectedFormId(formId);
  }, []);

  const onFieldSubmit = useCallback(
    async (formData: FormFields) => {
      const isEdit = fieldModal?.type === 'edit';
      const payload = createFieldPayload(
        formData,
        isEdit,
        fieldModal?.initialField?.id
      );
      if (isEdit) {
        return editField({
          ...payload,
          processId,
          fieldId: fieldModal!.initialField!.id,
        });
      } else {
        return createField({ ...payload, processId });
      }
    },
    [createField, editField, fieldModal, processId]
  );

  const handleSystemFieldsTableChange = createTableChangeHandler(
    systemTableControls,
    systemFields
  );

  const handleCustomFieldsTableChange = createTableChangeHandler(
    customTableControls,
    customFields
  );

  const customTableControlsProps = {
    currentPage: customTableControls.currentPage,
    pageSize: customTableControls.pageSize,
    searchQuery: customTableControls.searchQuery,
    setSearchQuery: customTableControls.setSearchQuery,
    onChange: handleCustomFieldsTableChange,
  };

  const customActions: ColumnAction[] = useMemo(
    () => [
      {
        key: 'edit',
        name: 'edit',
        label: t('list-view.action-items.edit'),
        onClick: handleEditField,
        permission: true,
      },
      {
        key: 'delete',
        name: 'delete',
        label: t('list-view.action-items.delete'),
        onClick: handleDeleteField,
        permission: true,
      },
    ],
    [t, handleEditField, handleDeleteField]
  );

  return (
    <div className={styles.dataFieldsContainer}>
      {/* System Fields Table */}
      <SystemFieldsTable
        systemFields={systemFields}
        isLoading={isSystemFieldsLoading}
        categoryName={categoryName}
        searchQuery={systemTableControls.searchQuery}
        setSearchQuery={systemTableControls.setSearchQuery}
        onChange={handleSystemFieldsTableChange}
      />

      {/* Custom Fields Table */}
      <CustomFieldsTable
        processId={processId}
        customFields={customFields}
        forms={formsData}
        isLoading={isCustomFieldsLoading}
        selectedFormId={selectedFormId}
        onFormSelect={handleFormSelect}
        onAddField={handleAddField}
        tableControls={customTableControlsProps}
        actions={customActions}
      />

      {/* Fields Modal for Add/Edit */}
      <FieldsModal
        type={fieldModal?.type || 'add'}
        open={!!fieldModal}
        closeModal={() => setFieldModal(undefined)}
        initialField={fieldModal?.initialField}
        onSubmit={onFieldSubmit}
        formsData={formsData}
      />

      {/* Delete Field Modal */}
      <DeleteFieldModal
        displayDeleteModal={!!fieldToDelete}
        onCancel={() => setFieldToDelete(undefined)}
        field={fieldToDelete}
        processId={processId}
      />
    </div>
  );
};
